import React, { useMemo, useState } from 'react';
import {
  AnalysisModel, Checkpoint, Granularity, Metric, MissingData, TrendResult,
  headToHead, isCompetitor, placeLabel, positionTrend,
} from '../../utils/majorAnalysis';
import { seriesColor } from '../../utils/chartPalette';
import { HolesAsk } from './DataNeeded';
import { PlayerCell, RankCell, Seg, SortTh, fmtScore, sortRows, useElementWidth, useSort } from './shared';

interface Props {
  model: AnalysisModel;
  visible: Set<number>;
  metric: Metric;
  missing: MissingData;
  onOpenRound: (roundId: number) => void;
}

// x positions for checkpoints, with a little air between rounds.
function xScale(checkpoints: Checkpoint[], left: number, right: number) {
  const ROUND_GAP = 1.4;
  const units: number[] = [];
  let u = 0;
  checkpoints.forEach((cp, i) => {
    if (i > 0) u += checkpoints[i - 1].round !== cp.round ? ROUND_GAP : 1;
    units.push(u);
  });
  const span = units[units.length - 1] || 1;
  return units.map((v) => (checkpoints.length === 1 ? (left + right) / 2 : left + (v / span) * (right - left)));
}

const ordinalShort = (n: number) => `${n}`;

const BumpChart: React.FC<{
  trend: TrendResult;
  model: AnalysisModel;
  visible: Set<number>;
  highlight: number | null;
  setHighlight: (id: number | null) => void;
}> = ({ trend, model, visible, highlight, setHighlight }) => {
  const [wrapRef, width] = useElementWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const players = new Map(model.players.map((p) => [p.player_id, p]));
  const rows = trend.rows.filter((r) => visible.has(r.player_id));
  const n = Math.max(trend.rows.length, 2);
  const narrow = width < 520;
  const padL = 30, padR = narrow ? 88 : 120, padT = 12, padB = 40;
  const rowH = narrow ? 24 : 28;
  const H = padT + (n - 1) * rowH + padB;
  const xs = xScale(trend.checkpoints, padL + 6, width - padR);
  const y = (pos: number) => padT + (pos - 1) * rowH;
  const last = trend.checkpoints.length - 1;

  // Round spans for the x axis.
  const rounds = Array.from(new Set(trend.checkpoints.map((c) => c.round))).map((r) => {
    const idx = trend.checkpoints.map((c, i) => (c.round === r ? i : -1)).filter((i) => i >= 0);
    return { r, from: xs[idx[0]], to: xs[idx[idx.length - 1]], first: idx[0], byHole: trend.holeLevel[r - 1] };
  });

  // Direct labels at the finish; tied places share one label.
  const endLabels = new Map<number, { names: string[]; tied: boolean }>();
  rows.forEach((r) => {
    const pos = r.positions[last];
    if (pos == null) return;
    const e = endLabels.get(pos) ?? { names: [], tied: r.tied[last] };
    e.names.push(players.get(r.player_id)?.first_name ?? '');
    endLabels.set(pos, e);
  });

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') { setActive((a) => Math.min(last, a == null ? 0 : a + 1)); e.preventDefault(); }
    if (e.key === 'ArrowLeft') { setActive((a) => Math.max(0, a == null ? last : a - 1)); e.preventDefault(); }
    if (e.key === 'Escape') setActive(null);
  };

  const leaderVal = (i: number) => {
    const vals = trend.rows.map((r) => r.values[i]).filter((v): v is number => v != null);
    return vals.length ? Math.min(...vals) : null;
  };

  return (
    <div className="ma-chart" ref={wrapRef}>
      <svg
        width={width} height={H} role="img" tabIndex={0} onKeyDown={onKey}
        aria-label={`Places through the major for ${rows.length} players. Use the table below for every value.`}
        onMouseLeave={() => { setActive(null); }}
      >
        {Array.from({ length: n }, (_, i) => (
          <g key={i}>
            <line x1={padL} x2={width - padR + 4} y1={y(i + 1)} y2={y(i + 1)} className="ma-grid" />
            <text x={padL - 8} y={y(i + 1) + 4} textAnchor="end" className="ma-axis-text">{ordinalShort(i + 1)}</text>
          </g>
        ))}
        {rounds.map((rd, i) => (
          <g key={rd.r}>
            {i > 0 && <line x1={(rounds[i - 1].to + rd.from) / 2} x2={(rounds[i - 1].to + rd.from) / 2} y1={padT - 6} y2={H - padB + 6} className="ma-axis" />}
            <text x={(rd.from + rd.to) / 2} y={H - 12} textAnchor="middle" className="ma-axis-label">R{rd.r}</text>
            {rd.byHole && trend.checkpoints.filter((c) => c.round === rd.r && !narrow && (c.thru === 9 || c.isRoundEnd) && c.holes === 18).map((c) => {
              const idx = trend.checkpoints.indexOf(c);
              return <text key={c.thru} x={xs[idx]} y={H - padB + 14} textAnchor="middle" className="ma-axis-text">{c.thru}</text>;
            })}
          </g>
        ))}
        {active != null && <line x1={xs[active]} x2={xs[active]} y1={padT - 6} y2={H - padB + 4} className="ma-crosshair" />}
        {rows.map((r) => {
          const p = players.get(r.player_id)!;
          const color = seriesColor(p.order);
          const dim = highlight != null && highlight !== r.player_id;
          const segs: React.ReactNode[] = [];
          for (let i = 1; i < r.positions.length; i++) {
            const a = r.positions[i - 1];
            const b = r.positions[i];
            if (a == null || b == null) continue;
            const cp = trend.checkpoints[i];
            // Jumping a round with no hole-by-hole data: dashed, it isn't measured.
            const unmeasured = cp.isRoundEnd && !trend.holeLevel[cp.round - 1] && trend.checkpoints[i - 1].round !== cp.round;
            segs.push(
              <line key={i} x1={xs[i - 1]} y1={y(a)} x2={xs[i]} y2={y(b)} stroke={color}
                strokeWidth={highlight === r.player_id ? 3 : 2} strokeLinecap="round"
                strokeDasharray={unmeasured && trend.holeLevel.some(Boolean) ? '4 4' : undefined} />,
            );
          }
          const path = r.positions.map((pos, i) => (pos == null ? null : `${xs[i]},${y(pos)}`)).filter(Boolean).join(' ');
          return (
            <g key={r.player_id} opacity={dim ? 0.18 : 1}
              onMouseEnter={() => setHighlight(r.player_id)} onMouseLeave={() => setHighlight(null)}>
              {segs}
              <polyline points={path} fill="none" stroke="transparent" strokeWidth={14} />
              {r.positions.map((pos, i) => (pos != null && (trend.checkpoints[i].isRoundEnd || trend.checkpoints.length <= 12)
                ? <circle key={i} cx={xs[i]} cy={y(pos)} r={4} fill={color} className="ma-dot" />
                : null))}
            </g>
          );
        })}
        {Array.from(endLabels.entries()).map(([pos, e]) => (
          <text key={pos} x={xs[last] + 10} y={y(pos) + 4} className="ma-end-label">
            {e.tied ? 'T' : ''}{pos} {e.names.join(', ')}
          </text>
        ))}
        {trend.checkpoints.map((cp, i) => {
          const left = i === 0 ? padL : (xs[i - 1] + xs[i]) / 2;
          const right = i === last ? width - padR : (xs[i] + xs[i + 1]) / 2;
          return <rect key={i} x={left} y={0} width={Math.max(right - left, 1)} height={H - padB + 8} fill="transparent" onMouseEnter={() => setActive(i)} />;
        })}
      </svg>
      {active != null && (
        <div className={`ma-tip${xs[active] > width * 0.55 ? ' ma-tip--left' : ''}`} style={{ left: xs[active] }} role="status">
          <div className="ma-tip-title">{trend.checkpoints[active].label}</div>
          {rows
            .filter((r) => r.positions[active] != null)
            .sort((a, b) => (a.positions[active] as number) - (b.positions[active] as number))
            .map((r) => {
              const p = players.get(r.player_id)!;
              const lead = leaderVal(active);
              const back = lead != null && r.values[active] != null ? (r.values[active] as number) - lead : null;
              return (
                <div key={r.player_id} className="ma-tip-row">
                  <span className="ma-swatch" style={{ background: seriesColor(p.order) }} />
                  <span className="ma-tip-pos">{placeLabel(r.positions[active], r.tied[active])}</span>
                  <span className="ma-tip-name">{p.first_name}</span>
                  <span className="ma-tip-val">{back == null ? '' : back < 0.05 ? 'leads' : `${fmtScore(back)} back`}</span>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

const GapChart: React.FC<{ gap: (number | null)[]; checkpoints: Checkpoint[]; aColor: string; bColor: string; aName: string; bName: string }> = ({ gap, checkpoints, aColor, bColor, aName, bName }) => {
  const [wrapRef, width] = useElementWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const vals = gap.filter((v): v is number => v != null);
  if (vals.length < 1) return null;
  const M = Math.max(2, Math.ceil(Math.max(...vals.map(Math.abs))));
  const padL = 34, padR = 16, padT = 14, padB = 30, H = 170;
  const xs = xScale(checkpoints, padL + 4, width - padR);
  const y = (v: number) => padT + ((M - v) / (2 * M)) * (H - padT - padB);
  const pts = gap.map((v, i) => (v == null ? null : [xs[i], y(v)] as const)).filter((p): p is readonly [number, number] => !!p);
  const line = pts.map((p) => `${p[0]},${p[1]}`).join(' ');
  const area = pts.length ? `M${pts[0][0]},${y(0)} ${pts.map((p) => `L${p[0]},${p[1]}`).join(' ')} L${pts[pts.length - 1][0]},${y(0)} Z` : '';
  const last = checkpoints.length - 1;
  return (
    <div className="ma-chart" ref={wrapRef}>
      <svg width={width} height={H} role="img" aria-label={`Stroke gap between ${aName} and ${bName} through the major`} onMouseLeave={() => setActive(null)}>
        <defs>
          <clipPath id="ma-gap-top"><rect x={0} y={0} width={width} height={y(0)} /></clipPath>
          <clipPath id="ma-gap-bottom"><rect x={0} y={y(0)} width={width} height={H} /></clipPath>
        </defs>
        {[M, 0, -M].map((t) => (
          <g key={t}>
            <line x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} className={t === 0 ? 'ma-axis' : 'ma-grid'} />
            <text x={padL - 6} y={y(t) + 4} textAnchor="end" className="ma-axis-text">{t > 0 ? `+${t}` : t}</text>
          </g>
        ))}
        <path d={area} fill={aColor} opacity={0.12} clipPath="url(#ma-gap-top)" />
        <path d={area} fill={bColor} opacity={0.12} clipPath="url(#ma-gap-bottom)" />
        <polyline points={line} fill="none" stroke="#334155" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {pts.length > 0 && <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r={4} fill="#334155" className="ma-dot" />}
        <text x={padL + 6} y={padT + 10} className="ma-axis-label">↑ {aName} ahead</text>
        <text x={padL + 6} y={H - padB - 6} className="ma-axis-label">↓ {bName} ahead</text>
        {Array.from(new Set(checkpoints.map((c) => c.round))).map((r) => {
          const idx = checkpoints.map((c, i) => (c.round === r ? i : -1)).filter((i) => i >= 0);
          return <text key={r} x={(xs[idx[0]] + xs[idx[idx.length - 1]]) / 2} y={H - 8} textAnchor="middle" className="ma-axis-label">R{r}</text>;
        })}
        {active != null && <line x1={xs[active]} x2={xs[active]} y1={padT} y2={H - padB} className="ma-crosshair" />}
        {checkpoints.map((_, i) => {
          const left = i === 0 ? padL : (xs[i - 1] + xs[i]) / 2;
          const right = i === last ? width - padR : (xs[i] + xs[i + 1]) / 2;
          return <rect key={i} x={left} y={0} width={Math.max(right - left, 1)} height={H} fill="transparent" onMouseEnter={() => setActive(i)} />;
        })}
      </svg>
      {active != null && gap[active] != null && (
        <div className={`ma-tip${xs[active] > width * 0.55 ? ' ma-tip--left' : ''}`} style={{ left: xs[active] }} role="status">
          <div className="ma-tip-title">{checkpoints[active].label}</div>
          <div className="ma-tip-row">
            {Math.abs(gap[active] as number) < 0.05 ? 'Level' : `${(gap[active] as number) > 0 ? aName : bName} by ${fmtScore(Math.abs(gap[active] as number))}`}
          </div>
        </div>
      )}
    </div>
  );
};

const PositionTrend: React.FC<Props> = ({ model, visible, metric, missing, onOpenRound }) => {
  const [granularity, setGranularity] = useState<Granularity>('hole');
  const [highlight, setHighlight] = useState<number | null>(null);
  const trend = useMemo(() => positionTrend(model, metric, granularity), [model, metric, granularity]);
  const holeTrend = useMemo(() => positionTrend(model, metric, 'hole'), [model, metric]);
  const { sort, toggle } = useSort();
  const competitors = model.players.filter(isCompetitor);
  const visibleComp = competitors.filter((p) => visible.has(p.player_id));
  const [aId, setA] = useState<number | null>(null);
  const [bId, setB] = useState<number | null>(null);
  const a = aId ?? visibleComp[0]?.player_id ?? null;
  const b = bId ?? visibleComp.find((p) => p.player_id !== a)?.player_id ?? null;

  if (!competitors.length || !trend.checkpoints.length) {
    return <div className="ma-empty"><div className="ma-empty-title">No released rounds to chart yet.</div></div>;
  }

  const anyHole = trend.holeLevel.some(Boolean);
  const rowsById = new Map(trend.rows.map((r) => [r.player_id, r]));
  const tableRows = sortRows(
    visibleComp, sort, (p) => p.order,
    (p, key) => rowsById.get(p.player_id)?.positions[Number(key.slice(2))] ?? null,
  );
  const h2h = a != null && b != null && a !== b ? headToHead(model, holeTrend, a, b) : null;
  const pa = model.players.find((p) => p.player_id === a);
  const pb = model.players.find((p) => p.player_id === b);
  const lastGap = h2h ? [...h2h.gap].reverse().find((v) => v != null) ?? null : null;
  const biggestLead = (sign: 1 | -1) => {
    if (!h2h) return null;
    let best: { v: number; i: number } | null = null;
    h2h.gap.forEach((v, i) => { if (v != null && v * sign > 0.05 && (!best || v * sign > best.v)) best = { v: v * sign, i }; });
    return best as { v: number; i: number } | null;
  };
  const leadA = biggestLead(1);
  const leadB = biggestLead(-1);

  return (
    <div className="ma-tab">
      <div className="ma-tab-bar">
        <p className="ma-lede">
          Where everyone stood after every {anyHole ? 'hole' : 'round'}, in each player’s own round order.
        </p>
        <Seg label="Granularity" value={granularity} onChange={setGranularity} options={[
          { value: 'hole', label: 'Hole' }, { value: 'nine', label: 'Nine' }, { value: 'round', label: 'Round' },
        ]} />
      </div>

      {trend.missing.length > 0 && (
        <div className="ma-note">
          <strong>{trend.missing.map((m) => `R${m.round}`).join(', ')}:</strong> places after the round only — hole-by-hole
          places need a complete card from everyone who played it
          {trend.missing.some((m) => m.players.length) && (
            <> (missing: {Array.from(new Set(trend.missing.flatMap((m) => m.players.map((n) => n.split(' ')[0])))).join(', ')})</>
          )}.
          {anyHole && ' Dashed lines cross a round without hole data.'}
        </div>
      )}
      {trend.approxRounds.length > 0 && (
        <div className="ma-note">
          <strong>{trend.approxRounds.map((r) => `R${r}`).join(', ')}:</strong> some holes have no par, so mid-round places use the
          course par spread over the holes. End-of-round places are exact.
        </div>
      )}

      <BumpChart trend={trend} model={model} visible={visible} highlight={highlight} setHighlight={setHighlight} />
      <div className="ma-legend" aria-label="Players">
        {visibleComp.map((p) => (
          <button key={p.player_id} type="button" className={`ma-legend-item${highlight === p.player_id ? ' is-on' : ''}`}
            onMouseEnter={() => setHighlight(p.player_id)} onMouseLeave={() => setHighlight(null)}
            onFocus={() => setHighlight(p.player_id)} onBlur={() => setHighlight(null)}>
            <span className="ma-swatch" style={{ background: seriesColor(p.order) }} />{p.name}
          </button>
        ))}
      </div>
      <p className="ma-foot">
        Running score = rounds completed + strokes so far + par for the holes still to play
        {metric === 'net' ? ' + the round’s handicap spread evenly over its holes' : ''}. After every round it equals the
        leaderboard total, so the last column is the final result.
      </p>

      <div className="ma-scroll">
        <table className="tbl tbl--sm ma-table ma-table--sticky">
          <thead>
            <tr>
              <SortTh label="Rank" sortKey="rank" sort={sort} onSort={toggle} className="c ma-stick-1" />
              <th className="ma-stick-2">Player</th>
              {trend.checkpoints.map((cp, i) => (
                <SortTh key={i} sortKey={`cp${i}`} sort={sort} onSort={toggle} className={`c${cp.isRoundEnd ? ' ma-col-end' : ''}`}
                  title={cp.label} label={cp.isRoundEnd ? `R${cp.round}` : `${cp.thru}`} />
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((p) => {
              const r = rowsById.get(p.player_id)!;
              return (
                <tr key={p.player_id}>
                  <RankCell p={p} all={model.players} />
                  <PlayerCell p={p} />
                  {r.positions.map((pos, i) => {
                    const prev = i > 0 ? r.positions[i - 1] : null;
                    const move = pos != null && prev != null ? prev - pos : 0;
                    return (
                      <td key={i} className={`c${trend.checkpoints[i].isRoundEnd ? ' ma-col-end' : ''}`}>
                        {placeLabel(pos, r.tied[i])}
                        {move !== 0 && <span className={move > 0 ? 'ma-up' : 'ma-down'} title={move > 0 ? `Up ${move}` : `Down ${-move}`}>{move > 0 ? '▲' : '▼'}{Math.abs(move)}</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {trend.missing.length > 0 && (
        <HolesAsk matches={missing.holeScores} onOpenRound={onOpenRound} unlocks="hole-by-hole places for those rounds" />
      )}

      <section className="ma-card">
        <div className="ma-card-head">
          <h3 className="ma-h3">Head-to-head</h3>
          <div className="ma-h2h-pick">
            <select aria-label="First player" value={a ?? ''} onChange={(e) => setA(Number(e.target.value))}>
              {competitors.map((p) => <option key={p.player_id} value={p.player_id}>{p.name}</option>)}
            </select>
            <span className="ma-vs">vs</span>
            <select aria-label="Second player" value={b ?? ''} onChange={(e) => setB(Number(e.target.value))}>
              {competitors.map((p) => <option key={p.player_id} value={p.player_id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        {h2h && pa && pb ? (
          <>
            <p className="ma-lede">
              {lastGap == null ? 'Not enough shared rounds to compare.' : Math.abs(lastGap) < 0.05
                ? `${pa.first_name} and ${pb.first_name} finished level.`
                : `${lastGap > 0 ? pa.first_name : pb.first_name} finished ${fmtScore(Math.abs(lastGap))} ahead (${metric}).`}
              {leadA && ` ${pa.first_name}’s biggest lead: ${fmtScore(leadA.v)} (${h2h.checkpoints[leadA.i].label}).`}
              {leadB && ` ${pb.first_name}’s biggest lead: ${fmtScore(leadB.v)} (${h2h.checkpoints[leadB.i].label}).`}
            </p>
            <GapChart gap={h2h.gap} checkpoints={h2h.checkpoints} aColor={seriesColor(pa.order)} bColor={seriesColor(pb.order)} aName={pa.first_name} bName={pb.first_name} />
            <p className="ma-foot">
              {h2h.holes.length
                ? <>Same course, same round ({h2h.holes.length} holes, gross): {pa.first_name} won {h2h.aWon}, {pb.first_name} won {h2h.bWon}, {h2h.halved} halved.
                  {h2h.biggestSwing && <> Biggest swing: R{h2h.biggestSwing.round} #{h2h.biggestSwing.hole} at {h2h.biggestSwing.course} — {h2h.biggestSwing.winner === 'a' ? pa.first_name : pb.first_name} by {h2h.biggestSwing.by}.</>}</>
                : anyHole
                  ? 'They never played the same course in the same round with hole scores, so holes can’t be compared one-to-one.'
                  : 'Hole-by-hole comparison appears once hole scores are added.'}
            </p>
          </>
        ) : (
          <p className="ma-lede">Pick two different players.</p>
        )}
      </section>
    </div>
  );
};

export default PositionTrend;
